import BenchlingConfig from 'BenchlingConfig';
import {getQueryParam} from 'BenchlingUtil';
import * as NavConstants from 'constants/NavConstants';
import EditorRoutes, {_editorBase} from 'foundation/EditorRoutes';
import {evaluateLDFlag} from 'launchdarkly/LaunchDarklyUtil';
import {makeNewDraftState} from 'llm/chat-library/ChatDraftState';
import {
  AISettingsTab,
  ChatSettingsTab,
  getAISidepanelState,
  InitialUI,
  openAgentFactoriesView,
  openChatSettingsModal,
  openSettings,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {openChat, openDraftChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {
  getAgentTypeFromQueryStringValue,
  getAgentTypeText,
  getDefaultAgentType,
  isAgentTypeEnabled,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import {buildChatSettingsModalState} from 'llm/routes/UrlUtils';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import DatadogUtil from 'performance/DatadogUtil';
import {RouteBundle} from 'routing/RouteConstants';

function getInitialUIFromQueryString(): InitialUI | undefined {
  const modalQueryValue = getQueryParam('modal');
  if (modalQueryValue == null) {
    return undefined;
  }
  const validValues = Object.values(InitialUI) as Array<string>;
  if (validValues.includes(modalQueryValue)) {
    return modalQueryValue as InitialUI;
  }
  return undefined;
}

/**
 * Parse the ?agent query param, validate it, show a warning if the requested agent is
 * unavailable, and open a draft chat with the best available agent.
 */
export function openDraftChatFromQueryString(): void {
  const agentQueryValue = getQueryParam('agent');
  const initialUI = getInitialUIFromQueryString();

  if (agentQueryValue != null) {
    const requestedAgentType = getAgentTypeFromQueryStringValue(agentQueryValue);
    if (requestedAgentType == null) {
      notifications.warn('The agent is not available. Contact Benchling Support if this issue persists.');
    } else if (!isAgentTypeEnabled(requestedAgentType)) {
      notifications.warn(
        `You do not have access to ${getAgentTypeText(requestedAgentType)} Agent. Contact your administrator for access.`
      );
    } else {
      openDraftChat(makeNewDraftState(requestedAgentType), {initialUI});
    }
    return;
  }

  const defaultAgentType = getDefaultAgentType();
  if (defaultAgentType == null) {
    notifications.warn('You do not have access to chat. Contact your administrator.');
    return;
  }

  openDraftChat(makeNewDraftState(defaultAgentType), {initialUI});
}

/**
 * Open the chat settings modal from a URL. Gated on LLM_ENABLE_CHAT_REDESIGN so the routes are inert
 * until the flag is on. Mirrors the tab/selected item from the URL into global client state
 * (buildChatSettingsModalState owns per-tab defaulting, and preserves the current group filter
 * across a same-tab re-navigation since the group is not encoded in the URL).
 */
function openChatSettingsModalRoute(tab: ChatSettingsTab, selectedItemId: string | null): void {
  if (evaluateLDFlag('LLM_ENABLE_CHAT_REDESIGN') !== true) {
    return;
  }
  DatadogUtil.startRUMView('chat_settings');
  // Carry the current in-modal group filter across a same-tab re-navigation (selecting a row or
  // going Back only changes the URL's selectedItemId); buildChatSettingsModalState resets it to the
  // tab default only when landing on the tab fresh.
  const previous = getAISidepanelState().chatSettingsModal;
  const modalState = buildChatSettingsModalState(tab, selectedItemId, previous);
  _editorBase(() => {
    openChatSettingsModal(modalState);
    // The modal is only mounted inside the AI sidepanel, so ensure that section is open.
    SidepanelActions.openSection(NavConstants.SECTIONS.AI);
  });
}

export const AIRoutes: RouteBundle = {
  domain: 'llm',
  routes: [
    // Chat settings modal (LLM_ENABLE_CHAT_REDESIGN). Only the tab and selected item are URL-driven;
    // the per-tab list filter (group) is owned by modal state, not the URL.
    {
      urlPattern: 'chats/settings/skills/:itemId',
      handler: (itemId: string): void => {
        openChatSettingsModalRoute(ChatSettingsTab.SKILLS, itemId);
      },
    },
    {
      urlPattern: 'chats/settings/skills',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.SKILLS, null);
      },
    },
    {
      urlPattern: 'chats/settings/agents/:itemId',
      handler: (itemId: string): void => {
        openChatSettingsModalRoute(ChatSettingsTab.AGENTS, itemId);
      },
    },
    {
      urlPattern: 'chats/settings/agents',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.AGENTS, null);
      },
    },
    {
      urlPattern: 'chats/settings/connectors/:itemId',
      handler: (itemId: string): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        openChatSettingsModalRoute(ChatSettingsTab.CONNECTORS, itemId);
      },
    },
    {
      urlPattern: 'chats/settings/connectors',
      handler: (): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        openChatSettingsModalRoute(ChatSettingsTab.CONNECTORS, null);
      },
    },
    {
      urlPattern: 'chats/settings/general',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.GENERAL, null);
      },
    },
    {
      urlPattern: 'chats/settings',
      handler: (): void => {
        openChatSettingsModalRoute(ChatSettingsTab.GENERAL, null);
      },
    },
    {
      urlPattern: 'ai-connectors',
      handler: (): void => {
        if (!isMcpAiConnectorsFeatureEnabled()) {
          return;
        }
        DatadogUtil.startRUMView('ai_connectors');
        _editorBase(() => {
          openSettings(AISettingsTab.AI_CONNECTORS);
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
      legacy: true,
    },
    {
      urlPattern: 'chats/new',
      handler: (): void => {
        DatadogUtil.startRUMView('chats_new');
        _editorBase(() => {
          openDraftChatFromQueryString();
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
    },
    // Agent Factories list: rendered in the sidepanel's main content slot (the same slot the chat
    // occupies). Must precede `chats/:id` below, or "agent-factories" would be swallowed as a chat id.
    {
      urlPattern: 'chats/agent-factories',
      handler: (): void => {
        if (!BenchlingConfig.LLM_ENABLE_AGENT_FACTORIES) {
          return;
        }
        DatadogUtil.startRUMView('agent_factories_list');
        _editorBase(() => {
          openAgentFactoriesView();
          SidepanelActions.expandSection(NavConstants.SECTIONS.AI);
        });
      },
    },
    {
      urlPattern: 'chats/:id',
      handler: (chatSessionId: string): void => {
        DatadogUtil.startRUMView('chats_existing');
        _editorBase(() => {
          openChat(chatSessionId);
        });
      },
    },
    {
      urlPattern: 'agent-factories/:agentFactoryId',
      handler: EditorRoutes.agentFactoryEditor.bind(EditorRoutes),
      legacy: false,
    },
  ],
};
